/** The hi-def backdrop of the town screen.
 *
 * STYLE (user direction, 2026-09-04): the town screen is the hi-def
 * showcase. The backdrop is a painterly 1920x1080 illustration in the manner
 * of the title key art, not pixel art, and it is drawn with linear filtering
 * at the canvas's 1280x720 - so at the 3/2 stage of a 1080p window every
 * painted pixel lands on exactly one device pixel, and at other sizes it is
 * resampled smoothly rather than snapped. `ScaledSprite` cannot do this: it
 * draws a frame at a whole multiple of its own size, which for a 1920-wide
 * frame is 1920 canvas pixels. This fits the frame into the canvas box
 * instead, through the same `spriteStyleBox` the theme's icons use, so the
 * atlas maths has one implementation.
 *
 * The building sprites take the same contract (townSlots.TOWN_HD): they ship
 * at their drawn size on a 1080p stage and the slot scales them by 1/TOWN_HD.
 */

import { useMemo } from "react";
import type { CSSProperties } from "react";
import { spriteStyleBox } from "../theme/atlas.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { TOWN_CANVAS_H, TOWN_CANVAS_W } from "./townSlots.js";

export function TownBackdrop(props: {
  readonly atlasKey: string;
  readonly title?: string;
  readonly style?: CSSProperties;
}): JSX.Element {
  const atlas = useAtlas();
  const frame = atlas.frame(props.atlasKey);
  const style = useMemo<CSSProperties>(() => {
    if (frame === null) {
      return { width: `${String(TOWN_CANVAS_W)}px`, height: `${String(TOWN_CANVAS_H)}px`, ...props.style };
    }
    return {
      ...(spriteStyleBox(atlas, props.atlasKey, TOWN_CANVAS_W, TOWN_CANVAS_H) as CSSProperties),
      ...props.style,
    };
  }, [atlas, frame, props.atlasKey, props.style]);
  return (
    <span
      className={`hm-icon ${frame === null ? "hm-icon--missing" : "hm-icon--hd"} hs-town__bg`}
      style={style}
      title={props.title}
      role="presentation"
    />
  );
}

/** The rules the hi-def stage owns, mounted by TownScreen beside its paint styles. */
export const SCENE_CSS = `
/* The painting and the buildings on it are hi-def art, resampled smoothly.
   styles.ts pixelates every .hs-town__art and the theme pixelates every
   .hm-icon--up; both are right for the map and wrong for a painting, where
   the nearest-neighbour snap at a 3/2 stage is a visible stagger down every
   roof line. One class more than each of the rules it replaces. */
.hs-town .hs-town__canvas .hs-town__bg { image-rendering: auto; }
.hs-town .hs-town__canvas .hs-town__slot .hs-town__art { image-rendering: auto; }
/* The backdrop is already graded by its painter: the per-faction saturate
   and brightness/contrast fitted to the quantised backdrops of 2026-09-01
   (townChrome --hs-bg-tone, --hs-grade) would over-saturate a painting that
   already carries its own light. Buildings keep their grade. */
.hs-town .hs-town__canvas[data-faction] .hs-town__bg { filter: none; }
`;
